import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navigation from './Navigation';
import LandingPage from './LandingPage';
import './App.css'; // App-specific styles, keep for global layout like background
import { loadGoogleScripts } from './utils/GoogleScripts';
import { TrackingErrorBoundary, useUserTracking } from './utils/UserTracking';

// Lazy load components
const ChatPageLayout = lazy(() => import('./ChatPageLayout'));
const SymptomChecker = lazy(() => import('./SymptomChecker'));
const ArticlesListing = lazy(() => import('./ArticlesListing'));
const ArticleTemplate = lazy(() => import('./ArticleTemplate'));

function CenteredLoading() {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
            color: 'white',
            fontSize: '1.5rem',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }}>
            <p>Loading Page...</p>
        </div>
    );
}

// Component to track route changes
function RouteTracker() {
    const location = useLocation();
    const { trackRouteChange } = useUserTracking();
    
    useEffect(() => {
        trackRouteChange(location);
    }, [location, trackRouteChange]);
    
    return null;
}

function App() {
    useEffect(() => {
        const setAppHeight = () => {
            document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
        };
        window.addEventListener('resize', setAppHeight);
        setAppHeight(); // Initial set

        // Defer loading of Google scripts
        loadGoogleScripts();

        return () => window.removeEventListener('resize', setAppHeight);
    }, []);

    return (
        <TrackingErrorBoundary name="App">
            <HelmetProvider>
                <Router>
                    <RouteTracker />
                    <div className="App">
                        <Navigation />
                        <div className="navigation-body-padding">
                            <Suspense fallback={<CenteredLoading />}>
                                <Routes>
                                    <Route path="/" element={<LandingPage />} />
                                    <Route path="/chat-interface" element={<ChatPageLayout />} />
                                    <Route path="/symptom-checker" element={<SymptomChecker />} />
                                    <Route path="/articles" element={<ArticlesListing />} />
                                    <Route path="/articles/:slug" element={<ArticleTemplate />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </div>
                </Router>
            </HelmetProvider>
        </TrackingErrorBoundary>
    );
}

export default App;